import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  BookOpen,
  Calendar,
  MessageCircle,
  User,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Clock,
  Heart,
  Send,
  Loader2,
  ChevronRight,
  Star,
  Trophy,
  Smile,
  Meh,
  Frown,
  FileText,
  Users,
  CalendarDays,
  Sparkles,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors and icons
const statusConfig = {
  yolunda: {
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    label: 'Yolunda'
  },
  destek_gerekli: {
    icon: AlertTriangle,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    label: 'Destek Gerekli'
  },
  dikkat_gerekli: {
    icon: AlertCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    label: 'Dikkat Gerekli'
  }
};

const emojiOptions = [
  { emoji: '😊', label: 'Çok İyi', value: '😊' },
  { emoji: '😐', label: 'Normal', value: '😐' },
  { emoji: '😢', label: 'Zor', value: '😢' }
];

const durationOptions = [
  { value: 0, label: 'Yapılmadı' },
  { value: 5, label: '5 dk' },
  { value: 10, label: '10 dk' },
  { value: 15, label: '15 dk' },
  { value: 20, label: '20 dk' },
  { value: 30, label: '30+ dk' }
];

export const ParentDashboard = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedChild, setSelectedChild] = useState(null);
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [report, setReport] = useState(null);
  const [logForm, setLogForm] = useState({
    student_id: '',
    log_date: new Date().toISOString().split('T')[0],
    status: 'tamamlandi',
    duration_minutes: 10,
    book_title: '',
    emoji_rating: '😊'
  });
  const [logLoading, setLogLoading] = useState(false);
  
  // Message state
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [messageLoading, setMessageLoading] = useState(false);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/parent/dashboard`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
        if (data.children?.length > 0 && !selectedChild) {
          setSelectedChild(data.children[0]);
          setLogForm(prev => ({ ...prev, student_id: data.children[0].student_id }));
        }
      } else {
        toast.error('Dashboard yüklenemedi');
      }
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchThreads = async () => {
    try {
      const response = await fetch(`${API_URL}/api/parent/messages/threads`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setThreads(data);
      }
    } catch (error) {
      console.error('Threads error:', error);
    }
  };

  const fetchMessages = async (threadId) => {
    try {
      setMessageLoading(true);
      const response = await fetch(`${API_URL}/api/parent/messages/threads/${threadId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
      }
    } catch (error) {
      console.error('Messages error:', error);
    } finally {
      setMessageLoading(false);
    }
  };

  const fetchReport = async (studentId) => {
    try {
      const response = await fetch(`${API_URL}/api/parent/report/${studentId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setReport(data);
        setShowReportDialog(true);
      }
    } catch (error) {
      toast.error('Rapor yüklenemedi');
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchThreads();
  }, []);

  useEffect(() => {
    if (selectedThread) {
      fetchMessages(selectedThread.thread_id);
    }
  }, [selectedThread]);

  const handleLogReading = async () => {
    setLogLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/parent/reading-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(logForm),
      });

      if (response.ok) {
        toast.success('Okuma kaydı eklendi!');
        setShowLogDialog(false);
        fetchDashboard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Kayıt eklenemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLogLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThread) return;

    try {
      const response = await fetch(`${API_URL}/api/parent/messages/threads/${selectedThread.thread_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newMessage }),
      });

      if (response.ok) {
        const msg = await response.json();
        setMessages(prev => [...prev, msg]);
        setNewMessage('');
      }
    } catch (error) {
      toast.error('Mesaj gönderilemedi');
    }
  };

  const handleRegisterWorkshop = async (workshopId) => {
    try {
      const response = await fetch(`${API_URL}/api/parent/workshops/${workshopId}/register`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Etkinliğe kaydınız yapıldı!');
        fetchDashboard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Kayıt yapılamadı');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Dashboard yüklenemedi</p>
      </div>
    );
  }

  const ChildCard = ({ child }) => {
    const status = statusConfig[child.overall_status] || statusConfig.yolunda;
    const StatusIcon = status.icon;
    const progressPercent = Math.min(100, (child.weekly_reading_days / child.weekly_goal_days) * 100);

    return (
      <Card 
        className={`cursor-pointer transition-all hover:shadow-md ${
          selectedChild?.student_id === child.student_id ? 'ring-2 ring-primary' : ''
        } ${status.borderColor} border-2`}
        onClick={() => {
          setSelectedChild(child);
          setLogForm(prev => ({ ...prev, student_id: child.student_id }));
        }}
        data-testid={`child-card-${child.student_id}`}
      >
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-semibold text-lg text-primary-900">{child.name}</h3>
              <p className="text-sm text-muted-foreground">
                {child.class_name || 'Sınıf atanmamış'}
              </p>
            </div>
            <div className={`p-2 rounded-full ${status.bgColor}`}>
              <StatusIcon className={`h-5 w-5 ${status.color}`} />
            </div>
          </div>

          <div className={`p-3 rounded-lg mb-4 ${status.bgColor}`}>
            <p className={`text-sm ${status.color}`}>
              {child.status_message}
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">Bu Hafta Okuma</span>
                <span className="font-medium">{child.weekly_reading_days}/{child.weekly_goal_days} gün</span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>

            {child.last_reading_date && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Son okuma: {new Date(child.last_reading_date).toLocaleDateString('tr-TR')}
              </p>
            )}

            {child.current_book && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <BookOpen className="h-3 w-3" />
                {child.current_book}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const ReadingLogDialog = () => (
    <Dialog open={showLogDialog} onOpenChange={setShowLogDialog}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Bugünkü Okuma
          </DialogTitle>
          <DialogDescription>
            3 adımda okuma kaydı ekleyin
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Step 1: Duration */}
          <div>
            <Label className="text-base font-medium">1. Ne kadar okudunuz?</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {durationOptions.map(opt => (
                <Button
                  key={opt.value}
                  type="button"
                  variant={logForm.duration_minutes === opt.value ? 'default' : 'outline'}
                  className="h-12"
                  onClick={() => setLogForm(prev => ({
                    ...prev,
                    duration_minutes: opt.value,
                    status: opt.value === 0 ? 'yapilmadi' : opt.value < 10 ? 'kismi' : 'tamamlandi'
                  }))}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Step 2: How did it go */}
          <div>
            <Label className="text-base font-medium">2. Nasıl geçti?</Label>
            <div className="flex justify-center gap-4 mt-2">
              {emojiOptions.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`text-4xl p-3 rounded-full transition-all ${
                    logForm.emoji_rating === opt.value
                      ? 'bg-primary/10 ring-2 ring-primary scale-110'
                      : 'hover:bg-slate-100'
                  }`}
                  onClick={() => setLogForm(prev => ({ ...prev, emoji_rating: opt.value }))}
                >
                  {opt.emoji}
                </button>
              ))}
            </div>
          </div>

          {/* Step 3: Book (optional) */}
          <div>
            <Label className="text-base font-medium">3. Ne okudunuz? (isteğe bağlı)</Label>
            <Input
              value={logForm.book_title}
              onChange={(e) => setLogForm(prev => ({ ...prev, book_title: e.target.value }))}
              placeholder="Kitap adı..."
              className="mt-2"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowLogDialog(false)} className="flex-1">
            İptal
          </Button>
          <Button onClick={handleLogReading} disabled={logLoading} className="flex-1">
            {logLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Kaydet
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  const ReportDialog = () => (
    <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            {report?.student_name} - İlerleme Raporu
          </DialogTitle>
          <DialogDescription>
            {report?.report_period}
          </DialogDescription>
        </DialogHeader>

        {report && (
          <div className="space-y-6 py-4">
            {/* Overall Status */}
            <div className={`p-4 rounded-lg ${statusConfig[report.overall_status]?.bgColor || 'bg-slate-50'}`}>
              <div className="flex items-center gap-3 mb-2">
                {React.createElement(statusConfig[report.overall_status]?.icon || CheckCircle, {
                  className: `h-6 w-6 ${statusConfig[report.overall_status]?.color || 'text-primary'}`
                })}
                <span className="font-semibold text-lg">
                  {statusConfig[report.overall_status]?.label || 'Yolunda'}
                </span>
              </div>
              <p className="text-muted-foreground">{report.status_explanation}</p>
            </div>

            {/* Strengths */}
            <div>
              <h4 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                <Star className="h-5 w-5" />
                Güçlü Yönler
              </h4>
              <ul className="space-y-1">
                {report.strengths.map((s, i) => (
                  <li key={i} className="text-sm flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>

            {/* Growth Areas */}
            <div>
              <h4 className="font-semibold text-amber-700 mb-2 flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                Gelişim Alanları
              </h4>
              <ul className="space-y-1">
                {report.growth_areas.map((g, i) => (
                  <li key={i} className="text-sm flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    {g}
                  </li>
                ))}
              </ul>
            </div>

            {/* Home Activities */}
            <div>
              <h4 className="font-semibold text-primary mb-2 flex items-center gap-2">
                <Heart className="h-5 w-5" />
                Evde Yapılabilecekler
              </h4>
              <ul className="space-y-1">
                {report.home_activities.map((a, i) => (
                  <li key={i} className="text-sm flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-primary flex-shrink-0" />
                    {a}
                  </li>
                ))}
              </ul>
            </div>

            {/* Reading Summary */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Okuma Özeti</h4>
              <p className="text-sm text-muted-foreground">{report.reading_summary}</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-6" data-testid="parent-dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-6">
        <h1 className="text-2xl font-heading font-bold text-primary-900">
          Hoş Geldiniz, {dashboard.guardian_name}!
        </h1>
        <p className="text-muted-foreground mt-1">
          Çocuğunuzun okuma yolculuğunu birlikte takip edelim.
        </p>
      </div>

      {/* Quick Stats */}
      {dashboard.children?.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <Users className="h-8 w-8 mx-auto text-primary mb-2" />
              <div className="text-2xl font-bold">{dashboard.children.length}</div>
              <p className="text-xs text-muted-foreground">Çocuk</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <BookOpen className="h-8 w-8 mx-auto text-green-600 mb-2" />
              <div className="text-2xl font-bold">
                {dashboard.children.reduce((sum, c) => sum + c.weekly_reading_days, 0)}
              </div>
              <p className="text-xs text-muted-foreground">Bu Hafta Okuma Günü</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <CalendarDays className="h-8 w-8 mx-auto text-secondary mb-2" />
              <div className="text-2xl font-bold">{dashboard.upcoming_workshops?.length || 0}</div>
              <p className="text-xs text-muted-foreground">Yaklaşan Etkinlik</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <MessageCircle className="h-8 w-8 mx-auto text-blue-600 mb-2" />
              <div className="text-2xl font-bold">{dashboard.unread_messages}</div>
              <p className="text-xs text-muted-foreground">Okunmamış Mesaj</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Genel</TabsTrigger>
          <TabsTrigger value="activities">Etkinlikler</TabsTrigger>
          <TabsTrigger value="workshops">Atölyeler</TabsTrigger>
          <TabsTrigger value="messages">Mesajlar</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Children Cards */}
          <div>
            <h2 className="text-lg font-semibold text-primary-900 mb-4">Çocuklarım</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dashboard.children?.map(child => (
                <ChildCard key={child.student_id} child={child} />
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          {selectedChild && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-secondary" />
                  {selectedChild.name} için Hızlı İşlemler
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Button 
                  onClick={() => setShowLogDialog(true)}
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  data-testid="log-reading-btn"
                >
                  <BookOpen className="h-6 w-6" />
                  <span className="text-sm">Okuma Kaydı</span>
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => fetchReport(selectedChild.student_id)}
                  className="h-auto py-4 flex flex-col items-center gap-2"
                >
                  <FileText className="h-6 w-6" />
                  <span className="text-sm">Rapor Görüntüle</span>
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setActiveTab('messages')}
                  className="h-auto py-4 flex flex-col items-center gap-2"
                >
                  <MessageCircle className="h-6 w-6" />
                  <span className="text-sm">Mesaj Gönder</span>
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Suggested Activities */}
          {selectedChild?.suggested_activities?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Heart className="h-5 w-5 text-red-500" />
                  Evde Yapılabilecekler
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {selectedChild.suggested_activities.map((activity, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <ChevronRight className="h-4 w-4 text-primary flex-shrink-0" />
                      {activity}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Activities Tab - Routine Cards */}
        <TabsContent value="activities" className="space-y-4">
          <h2 className="text-lg font-semibold text-primary-900">10 Dakikalık Ev Etkinlikleri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dashboard.weekly_routine_cards?.map((card, i) => (
              <Card key={card.card_id || i}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{card.title}</CardTitle>
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {card.duration_minutes} dk
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">{card.description}</p>
                  {card.instructions?.length > 0 && (
                    <ol className="text-sm space-y-1">
                      {card.instructions.slice(0, 3).map((step, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <span className="font-medium text-primary">{j + 1}.</span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Workshops Tab */}
        <TabsContent value="workshops" className="space-y-4">
          <h2 className="text-lg font-semibold text-primary-900">Veli Atölyeleri</h2>
          {dashboard.upcoming_workshops?.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Yaklaşan etkinlik bulunmuyor.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {dashboard.upcoming_workshops?.map(workshop => (
                <Card key={workshop.workshop_id}>
                  <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-primary-900">{workshop.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{workshop.description}</p>
                        <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(workshop.event_date).toLocaleDateString('tr-TR')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {workshop.start_time} - {workshop.end_time}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {workshop.current_registrations}/{workshop.max_capacity || '∞'} kayıt
                          </span>
                        </div>
                      </div>
                      <div>
                        {workshop.is_registered ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Kayıtlı
                          </Badge>
                        ) : workshop.can_register ? (
                          <Button onClick={() => handleRegisterWorkshop(workshop.workshop_id)}>
                            Kaydol
                          </Button>
                        ) : (
                          <Badge variant="secondary">Kapasite Dolu</Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-4">
          <h2 className="text-lg font-semibold text-primary-900">Mesajlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Thread List */}
            <Card className="md:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Sohbetler</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {threads.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    Henüz mesaj yok
                  </p>
                ) : (
                  threads.map(thread => (
                    <button
                      key={thread.thread_id}
                      className={`w-full p-3 rounded-lg text-left transition-colors ${
                        selectedThread?.thread_id === thread.thread_id
                          ? 'bg-primary/10'
                          : 'hover:bg-slate-50'
                      }`}
                      onClick={() => setSelectedThread(thread)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{thread.staff_name}</span>
                        {thread.unread_count > 0 && (
                          <Badge className="h-5 w-5 p-0 flex items-center justify-center">
                            {thread.unread_count}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{thread.subject}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {thread.student_name}
                      </p>
                    </button>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Message Content */}
            <Card className="md:col-span-2">
              {selectedThread ? (
                <>
                  <CardHeader className="pb-2 border-b">
                    <CardTitle className="text-base">{selectedThread.subject}</CardTitle>
                    <CardDescription>
                      {selectedThread.staff_name} • {selectedThread.student_name}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="h-64 overflow-y-auto space-y-3 mb-4">
                      {messageLoading ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                      ) : (
                        messages.map(msg => (
                          <div
                            key={msg.message_id}
                            className={`flex ${msg.direction === 'veliden' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] p-3 rounded-lg ${
                                msg.direction === 'veliden'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-slate-100'
                              }`}
                            >
                              <p className="text-sm">{msg.content}</p>
                              <p className={`text-xs mt-1 ${
                                msg.direction === 'veliden' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                              }`}>
                                {new Date(msg.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Mesajınızı yazın..."
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      />
                      <Button onClick={handleSendMessage} disabled={!newMessage.trim()}>
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </>
              ) : (
                <CardContent className="py-12 text-center text-muted-foreground">
                  Bir sohbet seçin
                </CardContent>
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <ReadingLogDialog />
      <ReportDialog />
    </div>
  );
};

export default ParentDashboard;
